import { useEffect, useState } from 'react';

import { Cancel, CheckCircle, Edit, Person, Send } from '@mui/icons-material';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import approveImage from 'src/assets/imgs/confirmImage.png';
import rejectImage from 'src/assets/imgs/warningImage.png';
import FullScreenLoader from 'src/components/FullscreenLoading';
import { If } from 'src/components/If';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  isMonitorPlanLimitError,
  UpgradeModal,
} from 'src/pages/Monitor/components/UpgradeModal';
import { useMonitorLimits } from 'src/pages/Monitor/hooks/useMonitorLimits';
import {
  WalletInvitation,
  WalletInvitationLinkStatus,
} from 'src/services/invite-monitor';
import {
  approveOrRejectPendingWallet,
  getGuestWalletByOwnerAndWalletId,
} from 'src/services/wallet';
import { Operation } from 'src/types/operation';
import { Wallet } from 'src/types/wallet';
import { getStrapiValidationMessage } from 'src/utils/getStrapiValidationMessage';

import { ApproveOrRejectPendingWalletDialog } from './ApproveOrRejectPendingWalletDialog';
import { GuestEnvironmentAlert } from './GuestEnvironmentAlert';
import { CancelSentWalletApprovalDialog } from './Guests/CancelSentWalletApprovalDialog';
import { GuestEnvironmentSelector } from './Guests/GuestEnvironmentSelector';
import { GuestViewFundsList } from './Guests/GuestViewFundsList';
import { SendWalletApprovalDialog } from './Guests/SendWalletApprovalDialog';
import { WalletSummaryCard } from './WalletSummaryCard';
import { useMonitorContext } from '../MonitorContextProvider';

type WalletInvitationLink = {
  id: number;
  status: WalletInvitationLinkStatus;
  createdAt: string;
  updatedAt: string;
  wallet: Wallet;
  invitation: WalletInvitation;
};

export default function MonitorOwnerGuestWallet() {
  const { auth } = useAuthContext();
  const { setGuestEnvironmentOwnerId } = useMonitorContext();
  const { limits } = useMonitorLimits();
  const { ownerId, walletId } = useParams<{
    ownerId: string;
    walletId: string;
  }>();
  const history = useHistory();

  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [link, setLink] = useState<Exclude<
    WalletInvitationLink,
    'invitation' | 'wallet'
  > | null>(null);
  const [openSendApprovalDialog, setOpenSendApprovalDialog] = useState(false);
  const [actionsToApproveOrReject, setActionsToApproveOrReject] = useState<{
    open: boolean;
    action: 'accepted' | 'rejected';
    invalidateQueries: string[][];
    image: string;
  }>({
    open: false,
    action: 'accepted',
    invalidateQueries: [],
    image: '',
  });
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [approvalUpgradeModal, setApprovalUpgradeModal] = useState<{
    open: boolean;
    apiMessage: string | null;
  }>({ open: false, apiMessage: null });
  const queryClient = useQueryClient();
  const [cancelSendApprovalDialog, setCancelSendApprovalDialog] =
    useState(false);

  const {
    data: queryData,
    isLoading: isLoadingGuestWallet,
    isFetched,
    isError,
  } = useQuery({
    queryKey: ['guest-wallet', ownerId, walletId],
    queryFn: () => getGuestWalletByOwnerAndWalletId({ ownerId, walletId }),
    onSuccess: (data) => {
      if (data) {
        setIsOwner(data.wallet.owner.id === auth?.user?.id);
        setWallet(data.wallet);
        setLink(data);
      }
    },
    onError: (error: Error) => {
      history.replace('/monitor');
      toast.error(error.message);
    },
    enabled: !!ownerId && !!walletId,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const {
    mutate: approveOrRejectPendingWalletMutation,
    isLoading: isLoadingApproveOrRejectPendingWallet,
  } = useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      id: string;
      action: 'accepted' | 'rejected';
    }) => approveOrRejectPendingWallet(id, action),
    onSuccess: () => {
      toast.success('Ação realizada com sucesso');
      actionsToApproveOrReject.invalidateQueries.forEach((queryKey) => {
        queryClient.invalidateQueries(queryKey);
      });
      setActionsToApproveOrReject({
        ...actionsToApproveOrReject,
        open: false,
      });
      history.replace('/monitor?tab=my-wallets');
    },
    onError: (
      error: unknown,
      variables: { id: string; action: 'accepted' | 'rejected' },
    ) => {
      const message = getStrapiValidationMessage(
        error as Parameters<typeof getStrapiValidationMessage>[0],
        '',
      );

      if (variables.action === 'accepted' && isMonitorPlanLimitError(message)) {
        setActionsToApproveOrReject((current) => ({
          ...current,
          open: false,
        }));
        setApprovalUpgradeModal({ open: true, apiMessage: message });
        return;
      }

      toast.error(
        message || 'Não foi possível concluir a ação. Tente novamente.',
      );
    },
  });

  useEffect(() => {
    if (!isLoadingGuestWallet && isFetched) {
      if (isError) {
        return;
      }

      const hasValidData =
        (queryData &&
          queryData.wallet &&
          queryData.invitation &&
          queryData.id) ||
        isOwner;

      if (!hasValidData) {
        history.replace('/monitor');
        toast.error('Acesso indevido ou link inválido');
      } else if (!isOwner) {
        setGuestEnvironmentOwnerId(ownerId);
      }
    }
  }, [isLoadingGuestWallet, isFetched, isError, queryData, history]);

  useEffect(() => {
    return () => {
      setGuestEnvironmentOwnerId(null);
    };
  }, []);

  const handleViewOperationFund = (operation: Operation) => {
    const { market, slug, hashId } = operation;
    history.push(`/operacao/${market.name.toLowerCase()}/${slug}/${hashId}`);
  };

  const walletLinkStatus = {
    draft: 'RASCUNHO',
    pending: 'PENDENTE',
    accepted: 'ACEITA',
    rejected: 'REJEITADA',
  };

  return (
    <>
      {!isOwner && (
        <>
          <GuestEnvironmentSelector />
          <GuestEnvironmentAlert />
        </>
      )}
      <Paper
        component="section"
        elevation={2}
        sx={{ backgroundColor: 'rgba(252, 221, 172, 0.1)' }}>
        <Box
          p={1}
          borderRadius={'5px'}
          display="flex"
          flexDirection="column"
          gap={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={4}>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="h5">
                {wallet?.name?.toUpperCase()}
              </Typography>
              {!isOwner && wallet?.creator && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Person sx={{ fontSize: '16px', color: 'text.disabled' }} />
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    fontWeight={600}>
                    {wallet?.owner?.fullName}
                  </Typography>
                </Box>
              )}
              {isOwner && wallet?.creator && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    fontWeight={600}>
                    Criado por:&nbsp;
                  </Typography>
                  <Person sx={{ fontSize: '16px', color: 'text.disabled' }} />
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    fontWeight={600}
                    mt={0.25}>
                    {wallet?.creator?.fullName}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box display="flex" width="30%" justifyContent="flex-end" gap={2}>
              <If condition={!isOwner}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Edit />}
                  onClick={() =>
                    history.push(`/monitor/${ownerId}/wallet/${walletId}/edit`)
                  }>
                  Editar Carteira
                </Button>
              </If>
              <If condition={!isOwner && link?.status === 'draft'}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Send />}
                  onClick={() => setOpenSendApprovalDialog(true)}>
                  Enviar para aprovação
                </Button>
              </If>
              <If condition={!isOwner && link?.status === 'pending'}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel sx={{ color: '#FF4747' }} />}
                  onClick={() => setCancelSendApprovalDialog(true)}>
                  Cancelar envio
                </Button>
              </If>
              <If condition={isOwner && link?.status === 'pending'}>
                <Box display="flex" gap={1}>
                  <Button
                    title="Aceitar"
                    size="small"
                    startIcon={<CheckCircle sx={{ color: '#82CE57' }} />}
                    sx={{ color: 'text.disabled' }}
                    onClick={() =>
                      setActionsToApproveOrReject({
                        open: true,
                        action: 'accepted',
                        invalidateQueries: [
                          ['guest-wallet', ownerId, walletId],
                          ['guest-wallets-summary-info', ownerId, 'pending'],
                        ],
                        image: approveImage,
                      })
                    }>
                    Aceitar
                  </Button>
                  <Button
                    title="Rejeitar"
                    size="small"
                    startIcon={<Cancel sx={{ color: '#FF4747' }} />}
                    sx={{ color: 'text.disabled' }}
                    onClick={() =>
                      setActionsToApproveOrReject({
                        open: true,
                        action: 'rejected',
                        invalidateQueries: [
                          ['guest-wallet', ownerId, walletId],
                          ['guest-wallets-summary-info', ownerId, 'pending'],
                        ],
                        image: rejectImage,
                      })
                    }>
                    Rejeitar
                  </Button>
                </Box>
              </If>
            </Box>
          </Box>
          <Box
            width="100%"
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap={2}
            px={1}
            pb={2}>
            <WalletSummaryCard
              data={{
                title: 'Status da Carteira',
                description: '',
                value:
                  walletLinkStatus[
                    link?.status as WalletInvitationLinkStatus
                  ] || '',
                color: '#fff',
              }}
            />
            <WalletSummaryCard
              data={{
                title: 'Total de fundos',
                description: 'Total de fundos na carteira',
                value: wallet?.walletFunds?.length || 0,
                color: '#fff',
              }}
            />
          </Box>
          <Box display="flex" flexDirection="column" gap={2} p={2}>
            <GuestViewFundsList
              walletFunds={wallet?.walletFunds || []}
              isLoading={isLoadingGuestWallet}
              onViewFund={({ operation }) => {
                handleViewOperationFund(operation);
              }}
            />
          </Box>
        </Box>
      </Paper>

      <FullScreenLoader
        open={isLoadingGuestWallet}
        message="Carregando carteira, aguarde..."
      />

      {wallet && (
        <>
          <SendWalletApprovalDialog
            open={openSendApprovalDialog}
            onClose={() => setOpenSendApprovalDialog(false)}
            walletId={walletId}
            walletName={wallet.name}
            invalidateQueries={[['guest-wallet', ownerId, walletId]]}
          />
          <CancelSentWalletApprovalDialog
            open={cancelSendApprovalDialog}
            onClose={() => setCancelSendApprovalDialog(false)}
            walletId={walletId}
            walletName={wallet.name}
            invalidateQueries={[['guest-wallet', ownerId, walletId]]}
          />
        </>
      )}

      <ApproveOrRejectPendingWalletDialog
        open={actionsToApproveOrReject.open}
        onClose={() =>
          setActionsToApproveOrReject({
            ...actionsToApproveOrReject,
            open: false,
          })
        }
        onConfirm={() =>
          approveOrRejectPendingWalletMutation({
            id: walletId,
            action: actionsToApproveOrReject.action,
          })
        }
        walletName={wallet?.name || ''}
        action={actionsToApproveOrReject.action}
        invalidateQueries={actionsToApproveOrReject.invalidateQueries}
        image={actionsToApproveOrReject.image}
        isLoading={isLoadingApproveOrRejectPendingWallet}
      />

      <UpgradeModal
        open={approvalUpgradeModal.open}
        onClose={() =>
          setApprovalUpgradeModal({ open: false, apiMessage: null })
        }
        type="approval"
        maxLimit={limits.maxWallets}
        currentCount={limits.usageWallets}
        apiMessage={approvalUpgradeModal.apiMessage}
        userName={auth?.user?.name}
        userId={auth?.user?.id}
      />
    </>
  );
}
